import { Fragment } from 'react';
import { Checkbox, Table } from 'semantic-ui-react';
import { UPDATA_STRATEGY } from '../../../../utils/reportUpdateManager';


type ChangeType = Record<string,any>

type FieldChangeType = {
    new: ChangeType;
    old: ChangeType;
};

type CellType = string | null | FieldChangeType;

type RowType = {
    descriptorId: string;
    dynamic: boolean;
    cells: CellType[];
};

type BatchUpdateTablePropsType = {
    strategiesEnabled: boolean;
    rows: RowType[];
    onUpdateStrategyChange?: () => void;
    descriptorsToUpdateStrategyMap?: Record<string, string[]>;
};

function BatchUpdateTable({
    strategiesEnabled,
    rows,
    onUpdateStrategyChange = () => {},
    descriptorsToUpdateStrategyMap = {},
}: BatchUpdateTablePropsType) {
    const headerCellColSpan = rows[0].cells.length;

    return (
        <Table celled>
            {strategiesEnabled && (
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell collapsing className="bg-gray">
                            <div>Preserve</div>
                            <div>CORE VALUES</div>
                        </Table.HeaderCell>
                        <Table.HeaderCell collapsing className="bg-gray">
                            <div>Mark</div>
                            <div>3D Calced.</div>
                        </Table.HeaderCell>
                        <Table.HeaderCell className="bg-gray" colSpan={headerCellColSpan} />
                    </Table.Row>
                </Table.Header>
            )}

            <Table.Body>
                {rows.map((row) => (
                    <Row
                        strategiesEnabled={strategiesEnabled}
                        key={row.descriptorId}
                        row={row}
                        onUpdateStrategyChange={onUpdateStrategyChange}
                        descriptorsToUpdateStrategyMap={descriptorsToUpdateStrategyMap}
                    />
                ))}
            </Table.Body>
        </Table>
    );
}

type RowProps = {
    row: RowType;
    strategiesEnabled: boolean;
    onUpdateStrategyChange: (descriptorId: string, strategy: string) => void;
    descriptorsToUpdateStrategyMap: Record<string, string[]>;
};

function Row({ row, strategiesEnabled, onUpdateStrategyChange, descriptorsToUpdateStrategyMap }: RowProps) {
    const currentStrategy = descriptorsToUpdateStrategyMap[row.descriptorId];

    return (
        <Table.Row>
            {strategiesEnabled && (
                <Fragment>
                    <Table.Cell textAlign="center">
                        {row.dynamic && (
                            <Checkbox
                                checked={currentStrategy.includes(UPDATA_STRATEGY.PRESERVE_NON_EMPTY_CORE_VALUES)}
                                onClick={() =>
                                    onUpdateStrategyChange(
                                        row.descriptorId,
                                        UPDATA_STRATEGY.PRESERVE_NON_EMPTY_CORE_VALUES
                                    )
                                }
                            />
                        )}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                        {row.dynamic && (
                            <Checkbox
                                checked={currentStrategy.includes(
                                    UPDATA_STRATEGY.APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES
                                )}
                                onClick={() =>
                                    onUpdateStrategyChange(
                                        row.descriptorId,
                                        UPDATA_STRATEGY.APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES
                                    )
                                }
                            />
                        )}
                    </Table.Cell>
                </Fragment>
            )}
            {row.cells.map((cell, cId) => (
                <Table.Cell key={cId}>
                    <CellData cell={cell} />
                </Table.Cell>
            ))}
        </Table.Row>
    );
}

function CellData({ cell }: { cell: CellType }) {
    let components = [];
    if (typeof cell === 'string' || cell === null) {
        components.push(<Fragment key="1">{cell}</Fragment>);
    } else {
        for (const prop of Object.keys(cell.new)) {
            const newData = cell.new[prop];
            const oldData = cell.old[prop];
            if (newData !== oldData) {
                components.push(
                    <Fragment key={prop}>
                        <p className="redText">
                            {prop}: {String(oldData)}
                        </p>
                        <p className="greenText">
                            {prop}: {String(newData)}
                        </p>
                    </Fragment>
                );
            }
        }
    }

    return components;
}

export default BatchUpdateTable;
